/**
 * Public homepage pain-point section — warm, honest framing of the problem MyLegacyBridge solves.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  FolderOpen,
  HeartHandshake,
  ImageIcon,
  KeyRound,
  Mic,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PUBLIC_DEMO_ROUTE } from '../../content/publicSiteContent';

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-48px' },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

const shell = 'mx-auto max-w-6xl px-4 sm:px-6 lg:px-8';
const card =
  'rounded-2xl border border-[hsl(38_16%_82%)] bg-[hsl(42_28%_99%)] shadow-[0_12px_40px_-24px_hsl(28_14%_18%_/_0.12)]';
const primaryBtn =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-[hsl(152_28%_22%)] px-6 text-sm font-semibold text-[hsl(42_30%_96%)] transition hover:bg-[hsl(152_28%_18%)]';
const outlineBtn =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-[hsl(38_16%_78%)] bg-white px-6 text-sm font-medium text-[hsl(24_14%_16%)] transition hover:border-[hsl(38_36%_42%)]';

export const HOME_PAIN_POINTS = [
  {
    icon: Mic,
    title: 'Their voice',
    line: 'The laugh. The accent. The way they told a story.',
    helper: 'Save voice memories beside their life story.',
  },
  {
    icon: ImageIcon,
    title: 'The story behind the photo',
    line: 'Photos lose meaning when nobody remembers who, where or why.',
    helper: 'Add names, places, captions and memories.',
  },
  {
    icon: Sparkles,
    title: 'The memories only they knew',
    line: 'Childhood stories, sayings, recipes and lessons can disappear quietly.',
    helper: 'Use gentle prompts to capture one memory at a time.',
  },
  {
    icon: FolderOpen,
    title: 'Everything scattered everywhere',
    line: 'Photos on phones. Documents in drawers. Stories in messages.',
    helper: 'Bring the important pieces into one private archive.',
  },
  {
    icon: HeartHandshake,
    title: 'A place to remember them properly',
    line: 'A life deserves more than scattered posts and forgotten timelines.',
    helper: 'Create a private memorial space with stories, tributes and voice.',
  },
  {
    icon: KeyRound,
    title: 'Practical things left unsaid',
    line: 'Families are often left guessing where things are and what mattered most.',
    helper: 'Leave clarity through the Family Handover Vault.',
  },
];

function PainPointCard({ item, index }) {
  const Icon = item.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.04 }}
      className={cn(card, 'flex h-full flex-col p-5')}
      data-testid={`home-pain-point-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[hsl(38_16%_82%)] bg-[hsl(43_32%_98%)] text-[hsl(38_36%_42%)]">
        <Icon className="h-4 w-4" strokeWidth={1.35} aria-hidden />
      </span>
      <h3 className="mt-3 font-serif text-xl font-medium leading-snug text-[hsl(24_14%_16%)]">
        {item.title}
      </h3>
      <p className="mt-2.5 text-[0.9375rem] leading-snug text-[hsl(24_14%_16%)]/92">{item.line}</p>
      <p className="mt-2 text-sm leading-relaxed text-[hsl(32_10%_38%)]">{item.helper}</p>
    </motion.article>
  );
}

export function HomePainPoints({ openCreateDialog }) {
  return (
    <section
      id="pain-points"
      className="scroll-mt-24 border-b border-[hsl(38_16%_82%)] bg-[hsl(42_28%_96%)] py-14 md:py-16"
      data-testid="home-pain-points-section"
    >
      <div className={shell}>
        <motion.div {...fadeUp} className="mx-auto max-w-3xl text-center">
          <p className="mb-2 text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-[hsl(38_36%_42%)]">
            Why families start
          </p>
          <h2 className="font-serif text-2xl font-light leading-snug text-[hsl(24_14%_16%)] sm:text-3xl">
            The things families wish they had saved
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-[hsl(32_10%_38%)] sm:text-base">
            A family archive is not only names and dates. It is the voice, stories, photos, memories and
            practical details that help future generations understand who someone really was.
          </p>
        </motion.div>

        <div className="mt-9 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {HOME_PAIN_POINTS.map((item, index) => (
            <PainPointCard key={item.title} item={item} index={index} />
          ))}
        </div>

        <motion.div {...fadeUp} className="mx-auto mt-10 max-w-2xl text-center">
          <div className="rounded-2xl border border-[hsl(38_16%_82%)]/75 bg-[hsl(42_28%_99%)]/90 px-5 py-5 sm:px-7 sm:py-6">
            <p className="font-serif text-xl leading-relaxed text-[hsl(24_14%_16%)] sm:text-[1.35rem]">
              One day, someone will ask what they were really like. MyLegacyBridge helps you preserve the answer.
            </p>
          </div>
          <div className="mt-7 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={openCreateDialog}
              className={primaryBtn}
              data-testid="home-pain-points-primary-cta"
            >
              Start your family archive
              <ArrowRight className="h-4 w-4" aria-hidden />
            </button>
            <Link
              to={PUBLIC_DEMO_ROUTE}
              className={outlineBtn}
              data-testid="home-pain-points-secondary-cta"
            >
              See how it works
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default HomePainPoints;
